import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { userProfileService, UserProfile, UserRole, AdminPrivilegeDetailsModel,AUTH_ERRORS } from '../services/axiosService';
import { configureUserProperties } from '../services/Heap3E';
import { trackEvent } from '../services/trackEvent';


// Types
interface UserContextType {
  userProfile: UserProfile | null;
  userRole: UserRole | null;
  isAdmin: boolean;
  profileLoading: boolean;
  profileError: string | null;
  refreshUserProfile: () => Promise<void>;
  getDisplayName: () => string;
  getAccount: (accountName: string) => any; 
}

interface UserProviderProps {
  children: ReactNode;
}

// Create context
const UserContext = createContext<UserContextType | undefined>(undefined);

// Custom hook to use user context
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// User Provider Component
export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { isAuthenticated, getAccessToken, user } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Fetch user profile data
  const fetchUserProfile = async () => {
    if (!isAuthenticated) {
      setUserProfile(null);
      setUserRole(null);
      setIsAdmin(false);
      setProfileError(null);
      return;
    }

    setProfileLoading(true);
    setProfileError(null);

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error(AUTH_ERRORS.NO_ACCESS_TOKEN);
      }

      // First check if user has admin role
      try {
        console.log('Fetching user role...');
        const role = await userProfileService.getUserRole(accessToken);
        console.log('User role response:', role);
        
        // Set the role and admin status
        setUserRole(role);
        setIsAdmin(role.isAdmin);
        
        console.log('Is user admin?', role.isAdmin);
        
        // If user is not an admin, log it but don't fetch profile
        if (!role.isAdmin) {
          console.log('User is not an admin, access denied');
          setProfileLoading(false);
          return;
        }
        
        // If user is an admin, proceed with fetching the profile
        const profile = await userProfileService.getUserProfile(accessToken);
        setUserProfile(profile);
        console.log('User profile fetched successfully:', profile);
      } catch (roleError) {
        console.error('Failed to fetch user role:', roleError);
        setProfileError(roleError instanceof Error ? roleError.message : "apiResponse.get-user-role.failedToFetchUserRole");
      }

      // This section is now handled above in the try block
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      setProfileError(error instanceof Error ? error.message : 'apiResponse.get-user-role.failedToFetchUserRole');
    } finally {
      setProfileLoading(false);
    }
  };

  // Fetch user profile when authenticated
  useEffect(() => {
    fetchUserProfile();
  }, [isAuthenticated]);

  // Configure Heap user properties when user info is available
  useEffect(() => {
    if (user && isAuthenticated) {
      configureUserProperties(user, userRole, userProfile, getDisplayName);
      
      // Track successful login
      trackEvent.loginSuccessful(userRole?.isAdmin ? 'Admin' : 'Basic User', {
        userId: user.sub || user.email,
        email: user.email,
        name: user.name || getDisplayName()
      });
    }
  }, [user, isAuthenticated, userRole, userProfile]);

  // Get display name for user
  const getDisplayName = () => {
    if (userProfile && (userProfile.firstName || userProfile.lastName)) {
      return `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim();
    }
    return user?.name || user?.email || 'User';
  };

  const getAccount = (applicationName: string) => {
      if (!userRole || !userRole.adminApplications) {
          return null;
      }
      return userRole.adminApplications.find((account: AdminPrivilegeDetailsModel) => account.applicationName === applicationName) || null;
  };

  const contextValue: UserContextType = {
    userProfile,
    userRole,
    isAdmin,
    profileLoading,
    profileError,
    refreshUserProfile: fetchUserProfile,
    getDisplayName,
    getAccount
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

export default UserProvider;